/** Dashboard locale identifiers. This module is safe to import in the browser. */
export const LOCALE_CODES = ["en", "de", "da", "es", "fr", "pt-BR", "zh-CN"] as const;
export type LocaleCode = (typeof LOCALE_CODES)[number];
export const LOCALES = [
  { code: "en", name: "English", direction: "ltr", intlLocale: "en" },
  { code: "de", name: "Deutsch", direction: "ltr", intlLocale: "de" },
  { code: "da", name: "Dansk", direction: "ltr", intlLocale: "da" },
  { code: "es", name: "Español", direction: "ltr", intlLocale: "es" },
  { code: "fr", name: "Français", direction: "ltr", intlLocale: "fr" },
  { code: "pt-BR", name: "Português (Brasil)", direction: "ltr", intlLocale: "pt-BR" },
  { code: "zh-CN", name: "简体中文", direction: "ltr", intlLocale: "zh-CN" },
] as const;

export type LocaleSource = "session" | "account" | "browser" | "dashboard" | "fallback";
export type LanguageRevision = { account: number; session: number };
export type LanguageContext = {
  sessionContext: string;
  locale: LocaleCode;
  source: LocaleSource;
  accountLocale: LocaleCode | null;
  sessionLocale: LocaleCode | null;
  dashboardDefaultLocale: LocaleCode | null;
  revision: LanguageRevision;
};

export const isLocaleCode = (value: unknown): value is LocaleCode =>
  typeof value === "string" && (LOCALE_CODES as readonly string[]).includes(value);

/** Compatibility normalization is deliberately separate from strict API writes. */
export const normalizeLocale = (value: unknown): LocaleCode | null => {
  if (typeof value !== "string") return null;
  const candidate = value.trim().toLowerCase();
  if (candidate === "dk") return "da";
  if (candidate === "pt") return "pt-BR";
  return LOCALE_CODES.find((code) => code.toLowerCase() === candidate) ?? null;
};

export const matchBrowserLocale = (languages: readonly string[]): LocaleCode | null => {
  for (const input of languages.slice(0, 20)) {
    if (input.length > 64) continue;
    let locale: Intl.Locale;
    try {
      locale = new Intl.Locale(input);
    } catch {
      continue;
    }
    const { language, region, script } = locale;
    if (["en", "de", "da", "es", "fr"].includes(language)) {
      if (script && script !== "Latn") continue;
      return language as LocaleCode;
    }
    if (language === "pt" && (!region || region === "BR") && (!script || script === "Latn")) return "pt-BR";
    if (language === "zh" && (!script || script === "Hans") && (!region || region === "CN" || region === "SG")) return "zh-CN";
  }
  return null;
};

export const resolveDashboardLocale = (input: {
  sessionLocale?: unknown;
  accountLocale?: unknown;
  browserLanguages?: readonly string[];
  dashboardDefaultLocale?: unknown;
}): { locale: LocaleCode; source: LocaleSource } => {
  const session = normalizeLocale(input.sessionLocale);
  if (session) return { locale: session, source: "session" };
  const account = normalizeLocale(input.accountLocale);
  if (account) return { locale: account, source: "account" };
  const browser = matchBrowserLocale(input.browserLanguages ?? []);
  if (browser) return { locale: browser, source: "browser" };
  const dashboard = normalizeLocale(input.dashboardDefaultLocale);
  if (dashboard) return { locale: dashboard, source: "dashboard" };
  return { locale: "en", source: "fallback" };
};

/** Bounded HTTP negotiation; zero-quality and malformed entries are ignored. */
export const parseAcceptLanguage = (header: string | undefined): string[] =>
  (header ?? "").slice(0, 2048).split(",").slice(0, 20)
    .map((part, index) => {
      const [tag, ...parameters] = part.trim().split(";");
      const weight = parameters.find((parameter) => parameter.trim().startsWith("q="));
      const q = weight === undefined ? 1 : /^\s*q=(?:0(?:\.\d{0,3})?|1(?:\.0{0,3})?)\s*$/.test(weight)
        ? Number(weight.trim().slice(2)) : 0;
      return { tag: tag?.trim() ?? "", q, index };
    })
    .filter(({ tag, q }) => tag.length > 0 && tag !== "*" && q > 0)
    .sort((a, b) => b.q - a.q || a.index - b.index)
    .map(({ tag }) => tag);
